import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import './CategoryManager.css';

function CategoryManager() {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    label: '',
    description: '',
    order: 0,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Fetch categories from Firestore
  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      try {
        const categoriesRef = collection(db, 'categories');
        const q = query(categoriesRef, orderBy('order', 'asc'));
        const snapshot = await getDocs(q);
        const categoriesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setFeedback({ type: 'error', message: 'Failed to load categories' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'order' ? parseInt(value) || 0 : value,
    }));
  };

  // Generate ID from label (lowercase, replace spaces with hyphens)
  const generateId = (label) => {
    return label
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.label.trim()) {
      setFeedback({ type: 'error', message: 'Category name is required' });
      return;
    }

    setIsSaving(true);
    setFeedback(null);

    try {
      const categoryId = formData.id || generateId(formData.label);
      const categoryData = {
        id: categoryId,
        label: formData.label.trim(),
        description: formData.description.trim() || '',
        order: formData.order || 0,
        updatedAt: serverTimestamp(),
      };

      if (editingCategory) {
        // Update existing category
        const categoryRef = doc(db, 'categories', editingCategory.id);
        await updateDoc(categoryRef, {
          ...categoryData,
          createdAt: editingCategory.createdAt, // Preserve original creation time
        });
        setFeedback({ type: 'success', message: 'Category updated successfully' });
      } else {
        // Add new category
        // Check if ID already exists
        const existingCategory = categories.find(cat => cat.id === categoryId);
        if (existingCategory) {
          setFeedback({ type: 'error', message: 'A category with this name already exists' });
          setIsSaving(false);
          return;
        }

        await addDoc(collection(db, 'categories'), {
          ...categoryData,
          createdAt: serverTimestamp(),
        });
        setFeedback({ type: 'success', message: 'Category added successfully' });
      }

      // Reset form
      setFormData({
        id: '',
        label: '',
        description: '',
        order: categories.length,
      });
      setEditingCategory(null);

      // Refresh categories list
      const categoriesRef = collection(db, 'categories');
      const q = query(categoriesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoriesData);

      // Clear feedback after 3 seconds
      setTimeout(() => setFeedback(null), 3000);
    } catch (error) {
      console.error('Error saving category:', error);
      setFeedback({ type: 'error', message: 'Failed to save category' });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle edit
  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      id: category.id,
      label: category.label,
      description: category.description || '',
      order: category.order || 0,
    });
    // Scroll to form
    document.querySelector('.category-form-container')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle delete
  const handleDelete = async (categoryId) => {
    setIsSaving(true);
    try {
      await deleteDoc(doc(db, 'categories', categoryId));
      setFeedback({ type: 'success', message: 'Category deleted successfully' });
      setShowDeleteConfirm(null);

      // Refresh categories list
      const categoriesRef = collection(db, 'categories');
      const q = query(categoriesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoriesData);

      setTimeout(() => setFeedback(null), 3000);
    } catch (error) {
      console.error('Error deleting category:', error);
      setFeedback({ type: 'error', message: 'Failed to delete category' });
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel edit
  const handleCancel = () => {
    setEditingCategory(null);
    setFormData({
      id: '',
      label: '',
      description: '',
      order: categories.length,
    });
    setFeedback(null);
  };

  if (isLoading) {
    return (
      <div className="category-manager">
        <div className="loading-state">Loading categories...</div>
      </div>
    );
  }

  return (
    <div className="category-manager">
      <div className="category-header">
        <h2>Category Manager</h2>
        <p>Manage product categories. Categories are used to organize products in your store.</p>
      </div>

      {feedback && (
        <div className={`feedback-message ${feedback.type}`}>
          {feedback.message}
        </div>
      )}

      {/* Category Form */}
      <div className="category-form-container">
        <h3>{editingCategory ? 'Edit Category' : 'Add New Category'}</h3>
        <form onSubmit={handleSubmit} className="category-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="label">Category Name *</label>
              <input
                type="text"
                id="label"
                name="label"
                value={formData.label}
                onChange={handleInputChange}
                placeholder="e.g., New Arrivals"
                required
                disabled={isSaving}
              />
              <small>This will be displayed to customers</small>
            </div>
            <div className="form-group">
              <label htmlFor="order">Display Order</label>
              <input
                type="number"
                id="order"
                name="order"
                value={formData.order}
                onChange={handleInputChange}
                min="0"
                disabled={isSaving}
              />
              <small>Lower numbers appear first</small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (Optional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Brief description of this category"
              rows="3"
              disabled={isSaving}
            />
          </div>

          <div className="form-actions">
            {editingCategory && (
              <button
                type="button"
                onClick={handleCancel}
                className="btn-cancel"
                disabled={isSaving}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="btn-save"
              disabled={isSaving || !formData.label.trim()}
            >
              {isSaving ? 'Saving...' : editingCategory ? 'Update Category' : 'Add Category'}
            </button>
          </div>
        </form>
      </div>

      {/* Categories List */}
      <div className="categories-list-container">
        <h3>Existing Categories ({categories.length})</h3>
        {categories.length === 0 ? (
          <div className="empty-state">
            <p>No categories yet. Add your first category above.</p>
          </div>
        ) : (
          <div className="categories-grid">
            {categories.map((category) => (
              <div key={category.id} className="category-card">
                <div className="category-card-header">
                  <div className="category-info">
                    <h4>{category.label}</h4>
                    {category.description && (
                      <p className="category-description">{category.description}</p>
                    )}
                    <div className="category-meta">
                      <span className="category-id">ID: {category.id}</span>
                      <span className="category-order">Order: {category.order || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="category-card-actions">
                  <button
                    onClick={() => handleEdit(category)}
                    className="btn-edit"
                    disabled={isSaving}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(category.id)}
                    className="btn-delete"
                    disabled={isSaving}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Category?</h3>
            <p>
              Are you sure you want to delete this category? This action cannot be undone.
              Products using this category will need to be updated manually.
            </p>
            <div className="modal-actions">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="btn-delete-confirm"
                disabled={isSaving}
              >
                {isSaving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryManager;




